import React, { useState } from 'react';
import axios from 'axios';
import { Archive, RotateCcw, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

export const matchesPatientStatus = (patient, status) =>
  !patient.merged_into_patient_id && (status === 'all' || (status === 'inactive' ? patient.is_inactive === true : patient.is_inactive !== true));

export function PatientStatusFilter({ value, onChange }) {
  return <label className="flex items-center gap-2 text-sm text-gray-700 mb-3">
    Situação
    <select aria-label="Situação dos pacientes" value={value} onChange={e => onChange(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 bg-white">
      <option value="active">Ativos</option><option value="inactive">Inativos</option><option value="all">Todos</option>
    </select>
  </label>;
}

export function PatientInactiveNotice({ patient }) {
  if (!patient?.is_inactive) return null;
  return <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3 my-2 text-sm">
    <strong>Paciente inativo — prontuário arquivado</strong>
    <p>O histórico está preservado. Reative o paciente para novos atendimentos.</p>
    {patient.inactive_at && <p>Inativado em {new Date(patient.inactive_at).toLocaleDateString('pt-BR')}.</p>}
    {patient.inactive_reason && <p>Motivo: {patient.inactive_reason}</p>}
  </div>;
}

export default function PatientStatusAction({ patient, onChanged }) {
  const { user, token } = useAuth();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!user?.permissions?.includes('patients.inactivate') || patient?.merged_into_patient_id) return null;
  const inactive = patient?.is_inactive === true;
  const Icon = inactive ? RotateCcw : Archive;
  const close = () => { if (!busy) { setOpen(false); setReason(''); } };
  const save = async () => {
    setBusy(true);
    let updated;
    try {
      const { data } = await axios.patch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/patients/${patient.id}/status`,
        { is_inactive: !inactive, reason: inactive ? null : reason.trim() || null },
        { headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` } });
      updated = data;
    } catch (error) {
      showToast(error?.response?.data?.detail || 'Não foi possível alterar a situação do paciente.', 'error');
      setBusy(false);
      return;
    }
    setBusy(false); setOpen(false); setReason('');
    showToast(inactive ? 'Paciente reativado!' : 'Paciente inativado. Histórico preservado.', 'success');
    await onChanged?.(updated);
  };
  return <>
    <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-amber-400 text-xs text-amber-800 hover:bg-amber-50" title={inactive ? 'Reativar paciente' : 'Inativar paciente'}>
      <Icon className="w-4 h-4" />{inactive ? 'Reativar' : 'Inativar'}
    </button>
    {open && <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4 text-left whitespace-normal" onClick={close}>
      <div role="dialog" aria-modal="true" aria-label={inactive ? 'Reativar paciente' : 'Inativar paciente'} className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center gap-2"><h2 className="text-lg font-semibold">{inactive ? 'Reativar paciente' : 'Inativar paciente'}</h2><button type="button" onClick={close} disabled={busy} aria-label="Fechar"><X className="w-5 h-5" /></button></div>
        <p className="mt-3 text-sm text-gray-700">{patient.full_name}</p>
        <p className="mt-2 text-sm text-gray-600">{inactive ? 'O paciente voltará às buscas e listas de atendimento.' : 'O paciente sairá das buscas padrão e do follow-up. Todo o prontuário, agendamentos e registros financeiros serão preservados.'}</p>
        {!inactive && <label className="block mt-4 text-sm text-gray-700">Motivo (opcional)<textarea autoFocus value={reason} onChange={e => setReason(e.target.value)} maxLength={500} rows={3} className="block mt-1 w-full border rounded-lg p-2" placeholder="Ex.: mudou de cidade, atendimento encerrado" /></label>}
        <div className="flex justify-end gap-2 mt-5"><button type="button" disabled={busy} onClick={close} className="px-4 py-2 border rounded-lg text-sm">Cancelar</button><button type="button" disabled={busy} onClick={save} className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm disabled:opacity-50">{busy ? 'Salvando…' : inactive ? 'Reativar paciente' : 'Inativar paciente'}</button></div>
      </div>
    </div>}
  </>;
}
